import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import MatrixRain from "@/components/MatrixRain";
import { useAuth } from "@/contexts/AuthContext";
import { NAV_LINKS } from "@/lib/navLinks";
import { Newspaper, ArrowUpRight, Trophy, Calculator, GitCompare, TrendingUp, BarChart3, ExternalLink, Loader2 } from "lucide-react";

const QUICK_LINKS = [
  { to: "/app/values", label: "DME Player Values", icon: BarChart3, desc: "The proprietary value board" },
  { to: "/app/power-rankings", label: "Power Rankings", icon: Trophy, desc: "Your league, ranked by DME" },
  { to: "/app/trade-calculator", label: "Trade Calculator", icon: Calculator, desc: "Balance any deal instantly" },
  { to: "/app/compare", label: "Compare Players", icon: GitCompare, desc: "Stack players side-by-side" },
  { to: "/app/market", label: "Market Trends", icon: TrendingUp, desc: "Risers & fallers, live" },
];

const SEO_RANKINGS = [
  { to: "/rankings/superflex/qb", label: "Superflex QB Rankings" },
  { to: "/rankings/superflex/rb", label: "Dynasty RB Rankings" },
  { to: "/rankings/1qb/wr", label: "Dynasty WR Rankings" },
  { to: "/rankings/superflex/te", label: "Dynasty TE Rankings" },
];

function timeAgo(ts) {
  if (!ts) return "";
  const d = typeof ts === "number" ? new Date(ts * (ts < 1e12 ? 1000 : 1)) : new Date(ts);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (isNaN(s)) return "";
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Home() {
  const [news, setNews] = useState(null);
  const { isPro } = useAuth();

  useEffect(() => {
    api.get("/news/wire", { params: { limit: 14 } })
      .then(r => setNews(r.data.items || []))
      .catch(() => setNews([]));
  }, []);

  return (
    <div className="relative fade-up" data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-lg border border-[var(--border)] terminal-grid mb-8">
        <MatrixRain columns={20} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-[var(--bg)]" />
        <div className="relative px-6 py-12 md:py-16">
          <div className="text-[var(--accent)] text-xs tracking-widest mb-2 font-mono">// DYNASTY METRICS HQ</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight max-w-2xl">The dynasty market, decoded.</h1>
          <p className="text-base text-[var(--text-2)] mt-3 max-w-xl">
            Your terminal for proprietary DME valuations, live trade intelligence and the latest dynasty news — all in one place.
          </p>
          <Link to="/app/values" className="btn-primary inline-flex items-center gap-2 mt-6 text-sm" data-testid="home-cta-values">
            Open the value board <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* All tools — full navigation launcher (primary nav on mobile) */}
      <section className="mb-8" data-testid="home-all-tools">
        <div className="text-xs tracking-widest text-[var(--text-3)] mb-3 font-mono">ALL TOOLS</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {NAV_LINKS.map(l => (
            <Link key={l.to} to={l.to} data-testid={`home-tab-${l.tid}`}
              className="card-bordered p-3.5 flex items-center gap-3 hover:border-[var(--accent)]/50 transition-colors group">
              <div className="w-9 h-9 rounded-sm bg-black/30 border border-[var(--border)] flex items-center justify-center shrink-0">
                <l.icon className="w-4 h-4 text-[var(--accent)]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm leading-tight truncate group-hover:text-[var(--accent)] transition-colors">{l.label}</div>
              </div>
              {l.pro && !isPro && <span className="text-[9px] font-mono tracking-widest text-[var(--accent)] border border-[var(--accent)]/40 rounded-sm px-1 py-0.5 shrink-0">PRO</span>}
            </Link>
          ))}
        </div>
      </section>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Dynasty news */}
        <section className="lg:col-span-2">
          <div className="flex items-center gap-2 text-xs tracking-widest text-[var(--text-3)] mb-3 font-mono">
            <Newspaper className="w-3.5 h-3.5 text-[var(--accent)]" /> DYNASTY WIRE
          </div>
          {news === null ? (
            <div className="card-bordered p-10 flex justify-center"><Loader2 className="w-5 h-5 spin text-[var(--accent)]" /></div>
          ) : news.length === 0 ? (
            <div className="card-bordered p-8 text-sm text-[var(--text-3)]">No fresh dynasty headlines right now — check back soon.</div>
          ) : (
            <div className="space-y-2" data-testid="home-news">
              {news.map((n, i) => (
                <a key={i} href={n.link || n.url} target="_blank" rel="noreferrer"
                  className="card-bordered p-4 flex items-start justify-between gap-3 hover:border-[var(--accent)]/50 transition-colors group block"
                  data-testid={`home-news-${i}`}>
                  <div className="min-w-0">
                    <div className="font-medium leading-snug group-hover:text-[var(--accent)] transition-colors">{n.title}</div>
                    <div className="text-[11px] tracking-widest text-[var(--text-3)] mt-1 uppercase">
                      {n.source || "Dynasty Wire"}{n.published || n.ts ? ` · ${timeAgo(n.published || n.ts)}` : ""}
                    </div>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-[var(--text-3)] shrink-0 mt-1" />
                </a>
              ))}
            </div>
          )}
        </section>

        {/* Quick links + SEO content */}
        <aside className="space-y-6">
          <div>
            <div className="text-xs tracking-widest text-[var(--text-3)] mb-3 font-mono">JUMP BACK IN</div>
            <div className="space-y-2">
              {QUICK_LINKS.map(q => (
                <Link key={q.to} to={q.to} className="card-bordered p-3 flex items-center gap-3 hover:border-[var(--accent)]/50 transition-colors group" data-testid={`home-link-${q.label}`}>
                  <div className="w-9 h-9 rounded-sm bg-black/30 border border-[var(--border)] flex items-center justify-center shrink-0">
                    <q.icon className="w-4 h-4 text-[var(--accent)]" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{q.label}</div>
                    <div className="text-[11px] text-[var(--text-3)] truncate">{q.desc}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs tracking-widest text-[var(--text-3)] mb-3 font-mono">PUBLIC RANKINGS</div>
            <div className="card-bordered p-4 space-y-2">
              {SEO_RANKINGS.map(s => (
                <Link key={s.to} to={s.to} className="flex items-center justify-between text-sm text-[var(--text-2)] hover:text-[var(--accent)] transition-colors" data-testid={`home-seo-${s.label}`}>
                  {s.label} <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
